export const dropdown = (config = {}) => ({
  id: config.id || "dropdown",
  name: config.name || "dropdown",
  required: config.required || false,
  labelId: config.labelId || null,
  placeholder: config.placeholder || "Select an option",
  options: [],
  selectedOption: null,
  isOpen: false,
  highlightedIndex: null,

  init() {
    // Initialize from existing select element if present, or from config
    if (config.selectId) {
      const selectElement = document.getElementById(config.selectId);
      if (selectElement) {
        this.id = selectElement.id;
        this.name = selectElement.name;
        this.required = selectElement.required;

        // Save all options but mark placeholder for special handling
        const allOptions = Array.from(selectElement.options);

        // Keep track of the placeholder option
        const placeholderOption = allOptions.find((option) => option.value === "");
        this.placeholder = placeholderOption ? placeholderOption.textContent : this.placeholder;

        // Filter out placeholder from dropdown options
        this.options = allOptions
          .filter((option) => option.value !== "")
          .map((option) => ({
            value: option.value,
            label: option.textContent,
            disabled: option.disabled,
          }));

        // Handle selected option
        if (selectElement.selectedIndex > -1) {
          const selectedOption = allOptions[selectElement.selectedIndex];
          if (selectedOption.value !== "") {
            this.selectedOption = this.options.find((o) => o.value === selectedOption.value);
          }
        }

        selectElement.style.display = "none";
      }
    } else {
      this.options = config.options || [];
      if (config.defaultValue) {
        this.selectByValue(config.defaultValue);
      }
    }

    // Listen for outside clicks to close dropdown
    this.$watch("isOpen", (value) => {
      if (value) {
        // Ensure we start with the currently selected option highlighted
        if (this.selectedOption) {
          this.highlightedIndex = this.options.findIndex((o) => o.value === this.selectedOption.value);
        }

        // Add event listener for tab key navigation
        window.addEventListener("keydown", this.handleTabKey);
      } else {
        window.removeEventListener("keydown", this.handleTabKey);
      }
    });
  },

  handleTabKey(e) {
    if (e.key === "Tab") {
      this.close();
    }
  },

  toggle() {
    this.isOpen ? this.close() : this.open();
  },

  open() {
    this.isOpen = true;
  },

  close() {
    this.isOpen = false;
    this.highlightedIndex = null;
  },

  highlight(index) {
    this.highlightedIndex = index;
  },

  highlightNext() {
    if (this.highlightedIndex === null) {
      this.highlightedIndex = 0;
    } else {
      this.highlightedIndex = (this.highlightedIndex + 1) % this.options.length;
    }
    this.scrollToHighlighted();
  },

  highlightPrev() {
    if (this.highlightedIndex === null) {
      this.highlightedIndex = this.options.length - 1;
    } else {
      this.highlightedIndex = (this.highlightedIndex - 1 + this.options.length) % this.options.length;
    }
    this.scrollToHighlighted();
  },

  scrollToHighlighted() {
    // Ensure the highlighted option is visible in the scrollable list
    this.$nextTick(() => {
      const highlighted = document.getElementById(`${this.id}-option-${this.highlightedIndex}`);
      if (highlighted) {
        highlighted.scrollIntoView({ block: "nearest" });
      }
    });
  },

  selectHighlighted() {
    if (this.isOpen && this.highlightedIndex !== null) {
      this.select(this.options[this.highlightedIndex]);
    } else {
      this.toggle();
    }
  },

  select(option) {
    this.selectedOption = option;
    this.updateNativeSelect();
    this.close();

    // Dispatch change event
    this.$nextTick(() => {
      this.$refs.select.dispatchEvent(new Event("change", { bubbles: true }));
    });
  },

  selectByValue(value) {
    const option = this.options.find((o) => o.value === value);
    if (option) {
      this.select(option);
    }
  },

  updateNativeSelect() {
    // Update the native select for form submission
    this.$nextTick(() => {
      if (this.selectedOption) {
        this.$refs.select.value = this.selectedOption.value;
      }
    });
  },
});
